import { arrow, flip, offset, Placement, shift, useFloating } from "@floating-ui/react-dom";
import { focusNextElement, getFocusableElements, getValue, handleRefHandlers } from "@utils/general";
import { getRectNormalized, getZoomLevel } from "@utils/zoom.utils";
import React, { CSSProperties, useCallback, useContext, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { DefaultTheme } from "styled-components";

import { AppContext, ContextEvents } from "../../contexts/appContext/AppContext.types";
import { KeyName } from "../../keyName";
import TestIds from "../../testIds";
import { Background, TooltipArrow } from "../tooltipIcon/TooltipIcon.styles";
import { StyledPopperTooltip } from "./PopperTooltip.styles";

export interface IProps {
    /** Content of the opened tooltip.
     * Can receive content as callback - performance optimization. We don't need to render tooltips that are not opened. */
    content: React.ReactNode | (() => React.ReactNode);
    /** Pass the reference element */
    children: (reference: React.Ref<any>) => React.ReactElement;
    /** Render in the current DOM instead of React.Portal to support different cases  */
    isHidden?: boolean;
    color?: string | keyof DefaultTheme;
    inPlace?: boolean;
    /** By default, if inPlace==false, tooltip is rendered via React.Portal into "context-menu-root",
     * use domPortalParent to choose custom DOM parent element */
    domPortalParent?: React.RefObject<HTMLElement>;
    useFade?: boolean;
    offsetY?: number;
    offsetX?: number;
    placement?: Placement;
    onAnimationEnd?: (event: React.AnimationEvent) => void;
    passRef?: React.Ref<HTMLDivElement>;
    className?: string;
    style?: React.CSSProperties;
    hideArrow?: boolean;
}

/** Tooltip with dark background and pointer arrow, positioned by Popper.js */
const PopperTooltip = React.memo((props: React.PropsWithChildren<IProps>) => {
    const appContext = useContext(AppContext);
    const arrowRef = useRef(null);

    const zoom = props.inPlace ? 1 : getZoomLevel();
    const zoomCorrectionMiddleware = {
        name: 'zoomCorrection',
        fn({ x, y }: { x: number; y: number }) {
            return {
                x: x / zoom,
                y: y / zoom,
            };
        },
    };

    const { refs, update, floatingStyles, middlewareData, placement } = useFloating({
        placement: props.placement ?? "top",
        middleware: [
            flip(),
            offset({ mainAxis: (props.offsetY ?? 0) * zoom, crossAxis: (props.offsetX ?? 0) * zoom }),
            shift(),
            arrow({ element: arrowRef }),
            zoomCorrectionMiddleware
        ]
    });

    const arrowStyle: CSSProperties = {
        left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : '',
        top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : '',
    };

    const referenceElement = (refs.reference?.current as HTMLElement);
    const popperElement = refs.floating?.current;

    const top = getRectNormalized(popperElement)?.height * ((1 / zoom) - 1);
    const left = (getRectNormalized(popperElement)?.width * ((1 / zoom) - 1)) / 2;
    const adjustAccordingToTooltipSize = {
        // todo: not sure why this is needed, but it is
        top: isNaN(top) || placement?.startsWith("bottom") ? 0 : top,
        left: isNaN(left) ? 0 : left,
    };

    const onPopperUpdate = useCallback(() => {
        if (!props.isHidden && update) {
            update();
        }
    }, [props.isHidden, refs]);

    useEffect(() => {
        appContext?.eventEmitter?.on(ContextEvents.UpdatePoppers, onPopperUpdate);

        return () => {
            appContext?.eventEmitter?.off(ContextEvents.UpdatePoppers, onPopperUpdate);
        };
    }, [appContext, onPopperUpdate]);

    /** If inPlace==false, default browser tabindex order is broken for keyboard navigation.
     * When use press Tab on the reference element, set the focus inside the PopperTooltip to enable keyboard navigation. */
    const onReferenceElementKeydown = useCallback((event: KeyboardEvent) => {
        if (!props.inPlace && !props.isHidden && event.key === KeyName.Tab && !event.shiftKey) {
            const focusableElements = getFocusableElements(popperElement);

            if (focusableElements?.length > 0) {
                event.stopPropagation();
                event.preventDefault();
                focusableElements[0]?.focus();
            }
        }
    }, [popperElement, props.isHidden, props.inPlace]);

    useEffect(() => {
        referenceElement?.addEventListener("keydown", onReferenceElementKeydown);

        return () => {
            referenceElement?.removeEventListener("keydown", onReferenceElementKeydown);
        };
    }, [referenceElement, onReferenceElementKeydown]);

    /** If inPlace==false, default browser tabindex order is broken for keyboard navigation.
     * When user press Tab inside the PopperTooltip and the focus should move out of it,
     * return it to the reference element or move it to the element after the reference element.*/
    const onPopperElementKeyDown = useCallback((event: KeyboardEvent) => {
        if (!props.inPlace && !props.isHidden && event.key === KeyName.Tab) {
            const focusableElements = getFocusableElements(popperElement);

            if (!event.shiftKey) {
                // forward
                if (document.activeElement === focusableElements[focusableElements.length - 1]) {
                    event.stopPropagation();
                    event.preventDefault();
                    focusNextElement(referenceElement);
                }
            } else {
                // backward
                if (document.activeElement === focusableElements[0] && referenceElement) {
                    event.stopPropagation();
                    event.preventDefault();
                    referenceElement.focus();
                }
            }
        }
    }, [popperElement, referenceElement, props.isHidden, props.inPlace]);

    useEffect(() => {
        if (!props.inPlace && !props.isHidden && popperElement) {
            popperElement?.addEventListener("keydown", onPopperElementKeyDown);
        }

        return () => {
            popperElement?.removeEventListener("keydown", onPopperElementKeyDown);
        };
    }, [props.inPlace, props.isHidden, popperElement, onPopperElementKeyDown]);

    const renderTooltip = (isGlobal?: boolean) => {
        return !props.isHidden &&
                (
                        <StyledPopperTooltip
                                // for HideOnBlur to work
                                tabIndex={-1}
                                isGlobal={isGlobal}
                                onClick={(e) => {
                                    // stop event here, to prevent e.g. triggering sort change event in table
                                    e.stopPropagation();
                                }}
                                useFade={props.useFade}
                                onAnimationEnd={props.onAnimationEnd}
                                ref={(ref) => {
                                    handleRefHandlers(ref, refs.setFloating, props.passRef);
                                }}
                                className={props.className}
                                style={{
                                    ...props.style,
                                    ...floatingStyles,
                                    ...adjustAccordingToTooltipSize
                                }}
                                data-popper-placement={placement}
                                data-testid={TestIds.Tooltip}>
                            {!props.hideArrow && <TooltipArrow ref={arrowRef}
                                    // for HideOnBlur to work
                                                               tabIndex={-1}
                                                               color={props.color}
                                                               style={arrowStyle}
                                                               data-popper-placement={placement}/>}
                            <Background color={props.color}/>
                            {getValue(props.content)}
                        </StyledPopperTooltip>
                );
    };

    const contextMenuRoot = !props.inPlace && (props.domPortalParent?.current ?? document.getElementById("context-menu-root"));
    const tooltip = contextMenuRoot ? ReactDOM.createPortal(renderTooltip(true), contextMenuRoot) : renderTooltip();

    return (
            <>
                {props.children?.(refs.setReference)}
                {tooltip}
            </>
    );
});

export default PopperTooltip;